import { zodResolver } from '@hookform/resolvers/zod';
import { ExpandMore as ChevronDown, Map as MapIcon, Save } from '@mui/icons-material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useEffect, useState } from 'react';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { CustomFieldsSection } from '@/components/location-form/custom-fields-section';
import { LocationMapManager } from '@/components/location-form/location-map-manager';
import { Page } from '@/components/page';
import { PartOfRelationshipManager } from '@/components/part-of-relationship-manager';
import {
  Location as DomainLocation,
  ImageManager,
  LocationFormSchema,
  LocationFormValues,
  useLocationAccessLayer,
} from '@/features/locations';
import { useImageProcessor } from '@/features/shared/hooks/use-image-processor';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useSubscription } from '@/hooks/useSubscription';
import { splitBySchemaShape } from '@/lib/utils/split-by-schema';

import { FloatingSaveButton } from '@/components/floating-save-button';

export const Route = createFileRoute('/locations/edit/$id')({
  component: EditLocationPage,
});

function EditLocationForm() {
  const { id: locationId } = Route.useParams();
  const navigate = useNavigate();
  const { novel, getItemRef } = useNovelRepository();
  const { hasFeature } = useSubscription();
  const { toast } = useToast();
  const [isDraggingPin, setIsDraggingPin] = useState(false);
  const resolveResponsive = useResponsiveValue();
  const { t } = useTranslation();

  const {
    location,
    sections: locationSettings,
    isLoading: isLoadingLayer,
    updateLocation: updateLocationLayer,
  } = useLocationAccessLayer(locationId);

  const { processImages, isProcessing: isProcessingImages } = useImageProcessor();
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingMap, setIsUploadingMap] = useState(false);
  const isProcessing = isProcessingImages;

  const methods = useForm<LocationFormValues>({
    resolver: zodResolver(LocationFormSchema),
    defaultValues: {
      name: '',
      shortDescription: '',
      type: 'Generic',
      region: '',
      images: [],
      mapData: null,
      pendingFiles: [],
      primaryImageId: null,
    },
  });

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = methods;

  const watchedImages = watch('images');
  const watchedPendingFiles = watch('pendingFiles');
  const watchedPrimaryImageId = watch('primaryImageId');

  const [lastResetId, setLastResetId] = useState<string | null>(null);

  // Sync images and primary UUID from background updates
  useEffect(() => {
    if (location?.images && JSON.stringify(location.images) !== JSON.stringify(methods.getValues('images'))) {
      setValue('images', location.images as LocationFormValues['images'], { shouldDirty: false });
    }
    if (location?.primaryImageId !== undefined && location.primaryImageId !== methods.getValues('primaryImageId')) {
      setValue('primaryImageId', location.primaryImageId, { shouldDirty: false });
    }
  }, [location?.images, location?.primaryImageId, setValue, methods]);

  useEffect(() => {
    if (location && !isLoadingLayer && lastResetId !== locationId) {
      reset(location as LocationFormValues);
      setLastResetId(locationId);
    }
  }, [location, isLoadingLayer, locationId, reset, lastResetId]);

  useEffect(() => {
    if (!isLoadingLayer && !novel.id) {
      navigate({ to: '/locations' });
    }
  }, [novel, isLoadingLayer, navigate]);

  if (!novel.id) {
    return null;
  }

  async function onCoreSubmit(values: LocationFormValues) {
    if (!novelId || !locationId || isUploadingMap) return;

    setIsSaving(true);
    try {
      const { pendingFiles, ...locationData } = values;
      const { defined: coreData, details } = splitBySchemaShape(LocationFormSchema, locationData);
      const coreDataForSave = { ...coreData };

      // Map uploads persist independently. Never write the form's older hidden
      // snapshot over a newly uploaded map image.
      delete coreDataForSave.mapImageUrl;
      delete coreDataForSave.mapImageStoragePath;
      delete coreDataForSave.originalMapUrl;
      delete coreDataForSave.originalMapStoragePath;
      delete coreDataForSave.mapLandUrl;
      delete coreDataForSave.mapLandStoragePath;
      delete coreDataForSave.mapColorUrl;
      delete coreDataForSave.mapColorStoragePath;
      delete coreDataForSave.mapSymbolsUrl;
      delete coreDataForSave.mapSymbolsStoragePath;
      delete coreDataForSave.mapData;
      delete coreDataForSave.placedSymbols;
      delete coreDataForSave.mapEntityPoints;
      delete coreDataForSave.mapEntityAreas;
      delete coreDataForSave.mapEntityPaths;
      delete coreDataForSave.mapLabels;
      delete coreDataForSave.mapWidth;
      delete coreDataForSave.mapHeight;

      let imagesUpdatedArray = [...(coreData.images || [])];

      // 1. Process images via shared hook
      if (pendingFiles && pendingFiles.length > 0) {
        const processedResults = await processImages(
          pendingFiles,
          novelId,
          `locations/${locationId}`,
          novel.ownerId
        );
        imagesUpdatedArray = [...imagesUpdatedArray, ...processedResults];
      }

      const primaryImageId = coreData.primaryImageId ?? imagesUpdatedArray[0]?.id ?? null;
      const mainImage = imagesUpdatedArray.find(image => image.id === primaryImageId);
      const mainImageUrl = mainImage?.profile?.url || mainImage?.original?.url || coreData.mapImageUrl || '';
      const mainImagePath = mainImage?.profile?.path || mainImage?.original?.path || coreData.mapImageStoragePath || '';

      const safeDetails = Object.fromEntries(
        Object.entries(details).map(([k, v]) => [k, String(v)])
      );

      // 2. Update via Access Layer
      await updateLocationLayer(locationId, {
        ...coreDataForSave,
        id: locationId,
        details: safeDetails,
        images: imagesUpdatedArray,
        primaryImageId,
        mainImageUrl,
        mainImagePath,
      });

      toast({
        title: t('entity_form.locations.update_success_title'),
        description: t('entity_form.locations.update_success_desc', { name: values.name }),
      });
      navigate({ to: '/locations' });
    } catch (error) {
      console.error('Update error:', error);
      toast({
        title: t('entity_form.common.update_failed_title'),
        description: error instanceof Error ? error.message : t('entity_form.common.unexpected_error'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  }

  const onInvalid = (validationErrors: FieldErrors<LocationFormValues>) => {
    console.warn('Location form validation errors:', validationErrors);
    const errorFields = Object.keys(validationErrors).map(key => {
      return key.charAt(0).toUpperCase() + key.slice(1);
    });

    toast({
      title: t('entity_form.common.validation_failed_title'),
      description: t('entity_form.common.validation_failed_desc', {
        fields: errorFields.join(', ')
      }),
      variant: 'destructive',
    });
  };

  const novelId = novel?.id;

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (location?.mapImageUrl) {
      e.preventDefault();
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (location?.mapImageUrl && e.dataTransfer.types.includes('application/json')) {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingPin(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPin(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPin(false);

    if (e.dataTransfer.types.includes('application/json') && location?.mapImageUrl) {
      const locationDataString = e.dataTransfer.getData('application/json');
      const locationData = JSON.parse(locationDataString);
      const mapRect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - mapRect.left) / mapRect.width) * 100;
      const y = ((e.clientY - mapRect.top) / mapRect.height) * 100;

      // Logic to update child location coordinates would go here
      alert(`Dropped ${locationData.name} at coordinates: x=${x.toFixed(2)}%, y=${y.toFixed(2)}%`);
      return;
    }
  };

  if (isLoadingLayer) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} thickness={4} />
      </Box>
    );
  }

  if (!location) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h3" color="text.secondary">{t('entity_view.locations.not_found_title')}</Typography>
        <Typography variant="body1">{t('entity_view.locations.not_found_desc')}</Typography>
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onCoreSubmit, onInvalid)}>
        <Page
          title={t('entity_form.locations.edit_dynamic_title', { name: location.name })}
          description={t('entity_form.locations.edit_desc')}
          breadcrumbs={[
            { label: t('entity_list.locations.title'), href: '/locations' },
            { label: location.name, href: `/locations/view/${location.id}` },
            { label: t('entity_form.common.edit') }
          ]}
          layoutMode="scrolling"
        >

          <Stack spacing={4}>
            {/* World Map Section */}
            {hasFeature('should_display_map_feature') && (
              <Accordion
                defaultExpanded={!!(location?.mapImageUrl || location?.mapData)}
                elevation={0}
                sx={{
                  borderRadius: '16px !important',
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ChevronDown />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <MapIcon sx={{ fontSize: 20 }} htmlColor="#a1824a" />
                    <Box>
                      <Typography variant="h3" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>{t('entity_form.locations.world_map_title')}</Typography>
                      <Typography variant="caption" color="text.secondary">{t('entity_form.locations.world_map_desc')}</Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, borderTop: '1px solid', borderColor: 'divider' }}>
                  {location && novelId && (
                    <LocationMapManager
                      location={location as DomainLocation}
                      disabled={isSaving}
                      onUploadingChange={setIsUploadingMap}
                      onMapImageUpdated={(updates) => {
                        setValue('mapImageUrl', updates.mapImageUrl, { shouldDirty: false });
                        setValue('mapImageStoragePath', updates.mapImageStoragePath, { shouldDirty: false });
                        setValue('originalMapUrl', updates.originalMapUrl, { shouldDirty: false });
                        setValue('originalMapStoragePath', updates.originalMapStoragePath, { shouldDirty: false });
                        if (updates.mainImageUrl !== undefined) {
                          setValue('mainImageUrl', updates.mainImageUrl, { shouldDirty: false });
                        }
                        if (updates.mainImagePath !== undefined) {
                          setValue('mainImagePath', updates.mainImagePath, { shouldDirty: false });
                        }
                      }}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      isDraggingPin={isDraggingPin}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            <Grid container spacing={4}>
              <Grid size={resolveResponsive({ xs: 12, lg: 8 })}>
                <Stack spacing={4}>
                  {/* Main Information */}
                  <Paper sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="h2" sx={{ mb: 1 }}>{t('entity_form.locations.main_info_title')}</Typography>
                        <Typography variant="body2" color="text.secondary">{t('entity_form.locations.main_info_desc')}</Typography>
                      </Box>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSaving || isUploadingMap}
                        startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 18 }} />}
                        sx={{ borderRadius: 2 }}
                      >
                        {t('entity_form.locations.save_changes')}
                      </Button>
                    </Box>

                    <Grid container spacing={3}>
                      <Grid size={resolveResponsive({ xs: 12 })}>
                        <TextField
                          fullWidth
                          label={t('entity_form.locations.labels.name')}
                          variant="filled"
                          {...register('name')}
                          error={!!errors.name}
                          helperText={errors.name?.message as string}
                          InputProps={{ disableUnderline: true, sx: { borderRadius: 2 } }}
                          InputLabelProps={{ shrink: true }}
                          required
                        />
                      </Grid>
                      <Grid size={resolveResponsive({ xs: 12, md: 6 })}>
                        <TextField
                          fullWidth
                          label={t('entity_form.locations.labels.type')}
                          variant="filled"
                          placeholder={t('entity_form.locations.labels.type_placeholder')}
                          {...register('type')}
                          error={!!errors.type}
                          helperText={errors.type?.message as string}
                          InputProps={{ disableUnderline: true, sx: { borderRadius: 2 } }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid size={resolveResponsive({ xs: 12, md: 6 })}>
                        <TextField
                          fullWidth
                          label={t('entity_form.locations.labels.region')}
                          variant="filled"
                          placeholder={t('entity_form.locations.labels.region_placeholder')}
                          {...register('region')}
                          error={!!errors.region}
                          helperText={errors.region?.message as string}
                          InputProps={{ disableUnderline: true, sx: { borderRadius: 2 } }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid size={resolveResponsive({ xs: 12 })}>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label={t('entity_form.locations.labels.short_desc')}
                          variant="filled"
                          placeholder={t('entity_form.locations.labels.short_desc_placeholder')}
                          {...register('shortDescription')}
                          error={!!errors.shortDescription}
                          helperText={errors.shortDescription?.message as string}
                          InputProps={{ disableUnderline: true, sx: { borderRadius: 2, p: 2 } }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid size={resolveResponsive({ xs: 12 })}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label={t('entity_form.locations.labels.visual_desc')}
                          variant="filled"
                          placeholder={t('entity_form.locations.labels.visual_desc_placeholder')}
                          {...register('visualDescription')}
                          error={!!errors.visualDescription}
                          helperText={errors.visualDescription?.message as string}
                          InputProps={{ disableUnderline: true, sx: { borderRadius: 2, p: 2 } }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Custom Fields Section */}
                  <CustomFieldsSection novelId={novelId} sections={locationSettings} />
                </Stack>
              </Grid>

              <Grid size={resolveResponsive({ xs: 12, lg: 4 })}>
                <Stack spacing={4}>
                  <ImageManager
                    location={{ images: watchedImages, primaryImageId: watchedPrimaryImageId }}
                    pendingFiles={watchedPendingFiles}
                    isProcessing={isProcessing}
                    entityId={locationId}
                    visualDescription={watch('visualDescription')}
                    onVisualDescriptionChange={(value) => setValue('visualDescription', value, { shouldDirty: true })}
                    onUpdate={(updates) => {
                      if (updates.images) setValue('images', updates.images);
                      if (updates.pendingFiles) setValue('pendingFiles', updates.pendingFiles);
                      if (updates.primaryImageId !== undefined) setValue('primaryImageId', updates.primaryImageId);
                    }}
                  />
                  <PartOfRelationshipManager location={location as DomainLocation} />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </Page>
        <FloatingSaveButton
          isSaving={isSaving}
          disabled={isUploadingMap}
          label={t('entity_form.locations.update')}
        />
      </form>
    </FormProvider>
  );
}

function EditLocationPage() {
  const { t } = useTranslation();
  return (
    <AuthGuard>
      <Suspense fallback={
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={40} thickness={4} />
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.2em' }}>
            {t('entity_form.locations.materializing')}
          </Typography>
        </Box>
      }>
        <EditLocationForm />
      </Suspense>
    </AuthGuard>
  );
}
